import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, signal } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { MatButton } from "@angular/material/button";
import { MatFormField } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatSelect } from "@angular/material/select";
import { MatOption } from "@angular/material/core";
import { MatCheckbox } from "@angular/material/checkbox";
import { DetectionService } from "../../../services/detection/detection.service";
import { AreaSummaryCardComponent } from "../../components/area-summary-card/area-summary-card.component";
import { ObjectInputComponent } from "../../../../shared-components/object-autocomplete/object-autocomplete.component";
import { ObjectRowComponent } from "../../components/object-row/object-row.component";
import {
  MAX_DETECTION_OBJECTS,
  MAX_DETECTION_TILES,
  MAX_SEARCH_BUFFER_M,
  SEARCH_BUFFER_STEP_M,
} from "../../../model/detection/detection.tokens";

const ZOOM_LEVELS = [
  { value: 21, label: "21 (Best)" },
  { value: 20, label: "20" },
  { value: 19, label: "19" },
  { value: 18, label: "18" },
  { value: 17, label: "17" },
  { value: 16, label: "16" },
];

@Component({
  selector: "lib-configure-panel",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: "flex-fill d-block overflow-hidden" },
  imports: [
    MatButton,
    MatFormField,
    MatInput,
    MatSelect,
    MatOption,
    MatCheckbox,
    AreaSummaryCardComponent,
    ObjectInputComponent,
    ObjectRowComponent,
  ],
  templateUrl: "./configure-panel.component.html",
  styleUrl: "./configure-panel.component.scss",
})
export class ConfigurePanelComponent {
  protected readonly detection = inject(DetectionService);

  protected readonly maxObjects = MAX_DETECTION_OBJECTS;

  protected readonly maxSearchBuffer = MAX_SEARCH_BUFFER_M;

  protected readonly searchBufferStep = SEARCH_BUFFER_STEP_M;

  protected readonly maxTiles = MAX_DETECTION_TILES;

  protected readonly zoomLevels = ZOOM_LEVELS;

  /** True when the panel is shown in processing state — inputs disabled, buttons hidden. */
  readonly disabled = input(false);

  /** Toggled by Ctrl+0 — reveals advanced tuning controls. */
  protected readonly showDevMode = signal(false);

  constructor() {
    const doc = inject(DOCUMENT);
    const destroyRef = inject(DestroyRef);

    const handler = (e: Event): void => {
      const ke = e as KeyboardEvent;
      if (ke.altKey && ke.key === "0") {
        ke.preventDefault();
        this.showDevMode.update((v) => !v);
      }
    };

    doc.addEventListener("keydown", handler);
    destroyRef.onDestroy(() => doc.removeEventListener("keydown", handler));
  }

  protected addObject(name: string): void {
    this.detection.addObject({ name, group: "Custom", icon: "category" });
  }

  protected onBufferInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const clamped = Math.min(Math.max(0, +inputElement.value || 0), MAX_SEARCH_BUFFER_M);
    if (+inputElement.value > clamped) {
      inputElement.value = String(clamped);
    }
  }
}
